import {
  collection,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/config';

// Store staff press a single "Help" icon and one of the designated responders
// (see HELP_RESPONDER_EMAILS) gets an on-screen popup + ding while they have
// the app open. Requests live in the `helpRequests` collection.

/** Kinds of request an employee can raise. */
export type RequestKind = 'help' | 'security';

/** Login emails of the people who receive general "help" pings. */
export const HELP_RESPONDER_EMAILS = [
  'zpalman@chorostandco.com',
  'tortega@chorostandco.com',
];

/** Login emails of the people who receive "security" pings. */
export const SECURITY_RESPONDER_EMAILS = [
  'jbarraza@chorostandco.com',
];

export function isHelpResponder(email?: string | null): boolean {
  if (!email) return false;
  return HELP_RESPONDER_EMAILS.includes(email.toLowerCase());
}

export function isSecurityResponder(email?: string | null): boolean {
  if (!email) return false;
  return SECURITY_RESPONDER_EMAILS.includes(email.toLowerCase());
}

/** Which request kinds this user should be alerted about.
 *  Note: help responders (leadership) also receive SECURITY pings — security is
 *  the most urgent event, so it should reach them in addition to the dedicated
 *  security responder(s), and it makes the red button verifiable from their own
 *  screen. */
export function responderKinds(email?: string | null): RequestKind[] {
  const kinds = new Set<RequestKind>();
  if (isHelpResponder(email)) {
    kinds.add('help');
    kinds.add('security');
  }
  if (isSecurityResponder(email)) kinds.add('security');
  return Array.from(kinds);
}

export type HelpRequestStatus = 'open' | 'resolved';

export interface HelpRequest {
  id: string;
  kind: RequestKind;
  requesterId: string;
  requesterName: string;
  requesterEmail: string;
  page: string;
  status: HelpRequestStatus;
  createdAt: Timestamp | null;
  resolvedAt: Timestamp | null;
  resolvedBy?: string;
  resolvedByName?: string;
}

const COLLECTION = 'helpRequests';

interface CreateHelpRequestParams {
  kind?: RequestKind;
  requesterId: string;
  requesterName: string;
  requesterEmail: string;
  page: string;
}

export async function createHelpRequest(params: CreateHelpRequestParams): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    kind: params.kind || 'help',
    requesterId: params.requesterId,
    requesterName: params.requesterName || 'Someone',
    requesterEmail: params.requesterEmail || '',
    page: params.page || '',
    status: 'open' as HelpRequestStatus,
    createdAt: serverTimestamp(),
    resolvedAt: null,
  });
  return ref.id;
}

/**
 * Subscribe to OPEN help requests. Uses a single equality filter (no composite
 * index required — that keeps it working even before any index finishes
 * building) and does recency filtering + sorting on the client.
 */
export function subscribeOpenHelpRequests(
  onChange: (requests: HelpRequest[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(collection(db, COLLECTION), where('status', '==', 'open'));
  return onSnapshot(
    q,
    (snap) => {
      const thirtyMinAgo = Date.now() - 30 * 60 * 1000;
      const rows = snap.docs
        .map((d) => {
          const data = d.data() as Omit<HelpRequest, 'id'>;
          return { id: d.id, ...data, kind: data.kind || 'help' };
        })
        // Only recent ones (ignore stale/forgotten open requests). Keep rows
        // whose timestamp hasn't resolved yet (serverTimestamp pending).
        .filter((r) => {
          const ms = r.createdAt?.toMillis?.() ?? null;
          return ms === null || ms >= thirtyMinAgo;
        })
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      onChange(rows);
    },
    (err) => {
      console.error('[HelpRequest] subscribeOpenHelpRequests error:', err);
      onError?.(err as Error);
    },
  );
}

export async function resolveHelpRequest(
  id: string,
  resolverId: string,
  resolverName: string,
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    status: 'resolved' as HelpRequestStatus,
    resolvedAt: serverTimestamp(),
    resolvedBy: resolverId,
    resolvedByName: resolverName || '',
  });
}
